
import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SocialAuth from "@/components/auth/SocialAuth";
import { supabase } from "@/integrations/supabase/client";
import SignUpForm from "@/components/auth/SignUpForm";
import { CheckCircle2 } from "lucide-react";
import { useReferralUrlParams } from "@/hooks/referrals/useReferralUrlParams";
import { useOAuthSignUpProcess } from "@/hooks/useOAuthSignUpProcess";
import { 
  getStoredStrategy, 
  clearStoredStrategy, 
  getRedirectUrl, 
  clearRedirectUrl 
} from "@/utils/strategySessionUtils";

const SignUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isInitialized, setIsInitialized] = useState(false);
  const fromPhemex = location.state?.fromPhemex || false;
  
  // Process URL parameters for referrals and affiliates
  useReferralUrlParams();
  
  // Process OAuth signup completions
  useOAuthSignUpProcess();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          // If redirected from Phemex and we have stored strategy, navigate to appropriate page
          const redirectUrl = getRedirectUrl();
          const strategyData = getStoredStrategy();
          
          if (fromPhemex && redirectUrl && strategyData) {
            // Clear stored data
            clearStoredStrategy();
            clearRedirectUrl();
            
            // Navigate to stored redirect with strategy data
            navigate(redirectUrl, { 
              state: { 
                strategy: strategyData,
                originalStrategyId: strategyData.id || strategyData.originalStrategyId
              } 
            });
          } else {
            // Default navigation after login
            navigate('/marketplace');
          }
        }
      } catch (error) {
        console.error("Error checking session:", error);
      } finally {
        setIsInitialized(true);
      }
    };
    
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        // Check if coming from Phemex and have stored strategy
        const redirectUrl = getRedirectUrl();
        const strategyData = getStoredStrategy();
        
        if (fromPhemex && redirectUrl && strategyData) {
          // Clear stored data
          clearStoredStrategy();
          clearRedirectUrl();
          
          // Navigate to stored redirect with strategy data
          navigate(redirectUrl, { 
            state: { 
              strategy: strategyData,
              originalStrategyId: strategyData.id || strategyData.originalStrategyId
            } 
          });
        } else {
          // Default navigation after login
          navigate('/marketplace');
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, fromPhemex]);

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-hero-dark to-hero-mid">
      <Header />
      <div className="container mx-auto px-4 py-8 md:py-12 flex flex-col md:flex-row items-center justify-center min-h-[calc(100vh-4rem)] gap-8">
        <div className="w-full max-w-md mx-auto md:mx-0 md:w-1/2 text-center md:text-left">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            Start Copy Trading Smarter with METRADE for free
          </h1>
          <p className="text-xl text-white/90 mb-6">
            Join our community of traders and gain access to expert-reviewed strategies tailored to your goals.
          </p>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mt-8 hidden md:block">
            <h3 className="text-xl font-semibold text-white mb-4">Why Join Metrade?</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <CheckCircle2 className="h-5 w-5 text-[#FFC300] mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-white">Access Free & Premium Strategies</span>
              </li>
              <li className="flex items-start">
                <CheckCircle2 className="h-5 w-5 text-[#FFC300] mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-white">Expert-Vetted Trading Strategies</span>
              </li>
              <li className="flex items-start">
                <CheckCircle2 className="h-5 w-5 text-[#FFC300] mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-white">Real-Time Performance Insights</span>
              </li>
              <li className="flex items-start">
                <CheckCircle2 className="h-5 w-5 text-[#FFC300] mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-white">Seamless Connection to Leading Exchanges</span>
              </li>
            </ul>
          </div>
        </div>
        
        <Card className="w-full max-w-md mx-auto md:mx-0 md:w-1/2 relative z-10 bg-white shadow-xl">
          <CardContent className="px-6 py-8">
            <div className="space-y-6">
              <SocialAuth />
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">Or continue with email</span>
                </div>
              </div>
              <SignUpForm />
            </div>
          </CardContent>
        </Card>
        
        {/* Mobile benefits section */}
        <div className="w-full max-w-md mx-auto mt-8 md:hidden">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
            <h3 className="text-xl font-semibold text-white mb-4 text-center">Why Join Metrade?</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <CheckCircle2 className="h-5 w-5 text-[#FFC300] mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-white">Access Free & Premium Strategies</span>
              </li>
              <li className="flex items-start">
                <CheckCircle2 className="h-5 w-5 text-[#FFC300] mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-white">Expert-Vetted Trading Strategies</span>
              </li>
              <li className="flex items-start">
                <CheckCircle2 className="h-5 w-5 text-[#FFC300] mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-white">Real-Time Performance Insights</span>
              </li>
              <li className="flex items-start">
                <CheckCircle2 className="h-5 w-5 text-[#FFC300] mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-white">Seamless Connection to Leading Exchanges</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SignUp;
